import axiosConfig from "./axiosConfig";

class AffiliationService {
  constructor() {
    this.baseUrl = "/v1/affiliations";
  }

  /**
     * Récupère les affiliations avec pagination, recherche, tri, filtres
     * @param {Object} params
     *  - page: numéro de page (défaut 1)
     *  - limit: nombre d'items par page (défaut 10)
     *  - search: recherche (optionnel)
     *  - country: filtre pays (optionnel)
     *  - type: filtre type (optionnel)
     *  - order_by: champ tri (défaut 'name_aff')
     *  - order_direction: 'asc' ou 'desc' (défaut 'asc')
     * @returns {Promise<Object>} Réponse paginée
     */
  async getAll({
    page = 1,
    limit = 10000,
    search = "",
    country_aff = "",
    type_aff = "",
    order_by = "name_aff",
    order_direction = "asc",
  } = {}) {
    try {
      const query = [
        `page=${page}`,
        `limit=${limit}`,
        `order_by=${order_by}`,
        `order_direction=${order_direction}`,
        search ? `search=${encodeURIComponent(search)}` : "",
        country_aff ? `country_aff=${encodeURIComponent(country_aff)}` : "",
        type_aff ? `type_aff=${encodeURIComponent(type_aff)}` : "",
      ]
        .filter(Boolean)
        .join("&");

      const response = await axiosConfig.get(
        `v2/affiliations?${query}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  /**
   * Crée une nouvelle affiliation
   * @param {Object} affiliation Données de l'affiliation
   * @returns {Promise<Object>} Affiliation créée
   */
  async createAffiliations(affiliation) {
    try {
      const formData = new FormData();
      formData.append("name_aff", affiliation.name_aff);
      formData.append("country_aff", affiliation.country_aff);
      formData.append("type_aff", affiliation.type_aff);

      const response = await axiosConfig.post(this.baseUrl, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  /**
   * Met à jour une affiliation existante
   * @param {number} id ID de l'affiliation
   * @param {Object} affiliation Nouvelles données
   * @returns {Promise<Object>} Affiliation mise à jour
   */
  async updateAffiliation(id, affiliation) {
    try {
      const formData = new FormData();
      formData.append("_method", "PUT");

      if (affiliation.name_aff)
        formData.append("name_aff", affiliation.name_aff);
      if (affiliation.country_aff)
        formData.append("country_aff", affiliation.country_aff);
      if (affiliation.type_aff)
        formData.append("type_aff", affiliation.type_aff);
      if (affiliation.is_valid !== undefined)
        formData.append("is_valid", affiliation.is_valid);

      const response = await axiosConfig.post(
        `${this.baseUrl}/${id}`,
        formData,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        "❌ Erreur lors de la mise à jour:",
        error.response?.data || error.message
      );
      throw error.response?.data || error.message;
    }
  }

  /**
   * Supprime une affiliation
   * @param {number} id ID de l'affiliation
   * @returns {Promise<Object>} Résultat de la suppression
   */
  async deleteaffiliation(id) {
    try {
      // Vérifier d'abord si l'affiliation existe
      await this.getAffiliationById(id);
      const response = await axiosConfig.delete(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  /**
   * Récupère une affiliation par son ID
   * @param {number} id ID de l'affiliation
   * @returns {Promise<Object>} L'affiliation
   */
  async getAffiliationById(id) {
    try {
      const response = await axiosConfig.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  /**
   * Crée plusieurs affiliations en parallèle
   * @param {Array<Object>} affiliations Liste des affiliations à créer
   * @returns {Promise<Array>} Résultats des créations
   */
  async createMultipleAffiliations(affiliations) {
    try {
      const promises = affiliations.map((affiliation) =>
        this.createAffiliations(affiliation)
      );
      return await Promise.all(promises);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Met à jour plusieurs affiliations en parallèle
   * @param {Array<Object>} updateData Tableau d'objets {id, affiliation}
   * @returns {Promise<Array>} Résultats des mises à jour
   */
  async updateMultipleAffiliations(updateData) {
    try {
      const promises = updateData.map(({ id, affiliation }) =>
        this.updateAffiliation(id, affiliation)
      );
      return await Promise.all(promises);
    } catch (error) {
      throw error;
    }
  }
}

const AffiliationServiceInstance = new AffiliationService();
export default AffiliationServiceInstance;
